/**
 * Unified Route Mapping Architecture
 * ─────────────────────────────────────────────────────────
 * Central source of truth for all internal routes.
 *
 * Structure:
 * - key: Unique, slugified identifier (derived from marketingUrl)
 * - marketingUrl: Clean URL visible in browser (for sitemap/SEO)
 * - implementedPath: Actual Next.js physical route
 * - gatsbyLegacyUrl: Old Gatsby site URL for redirects
 * - label: Human-readable page title
 * - category: Page category (used by footer, header, etc.)
 * - matchSubpaths: Whether dynamic child routes should be mapped
 */

export interface RouteMappingEntry {
  /** Unique identifier: slugified version of marketingUrl (e.g., 'home', 'speciality-cardiology') */
  key: string;
  /** Clean URL requested by marketing and visible in browser (e.g., '/about-lips') */
  marketingUrl: string;
  /** Actual Next.js physical route in codebase (e.g., '/about-us') */
  implementedPath: string;
  /** Old Gatsby legacy URL (e.g., '/about-us') for redirect context */
  gatsbyLegacyUrl?: string;
  /** Human-readable page label (e.g., 'About LIPS Healthcare') */
  label: string;
  /** Page category for grouping (homepage, speciality, information, legal, etc.) */
  category:
    | 'homepage'
    | 'speciality'
    | 'information'
    | 'legal'
    | 'specialist'
    | 'condition'
    | 'other';
  /** Set to true if dynamic subpaths (e.g., /our-specialists/:slug) should also be mapped */
  matchSubpaths?: boolean;
}

// Helper to generate key from URL
// function generateKeyFromUrl(url: string): string {
//   return url
//     .toLowerCase()
//     .split('/')
//     .filter(Boolean) // Remove empty strings
//     .join('-')
//     .replace(/[^a-z0-9-]/g, ''); // Remove special characters
// }

// ═══════════════════════════════════════════════════════════════════════════════
// ROUTE MAPPING - Single source of truth for all pages
// ═══════════════════════════════════════════════════════════════════════════════

export const ROUTES_MAPPING: RouteMappingEntry[] = [
  // ── HOMEPAGE ──────────────────────────────────────────────────────────────
  {
    key: 'home',
    marketingUrl: '/',
    implementedPath: '/',
    gatsbyLegacyUrl: '/',
    label: 'Homepage',
    category: 'homepage',
  },

  // ── ABOUT LIPS ────────────────────────────────────────────────────────────
  {
    key: 'about-lips',
    marketingUrl: '/about-lips',
    implementedPath: '/about-us',
    gatsbyLegacyUrl: '/about-us',
    label: 'About LIPS Healthcare',
    category: 'information',
  },
  {
    key: 'getting-here',
    marketingUrl: '/about-lips/getting-here',
    implementedPath: '/getting-here',
    gatsbyLegacyUrl: '/getting-here',
    label: 'How to Get Here',
    category: 'information',
  },
  {
    key: 'clinicians-and-referrals',
    marketingUrl: '/about-lips/clinicians-and-referrals',
    implementedPath: '/careers',
    gatsbyLegacyUrl: '/for-clinicians',
    label: 'Clinicians & Referrals',
    category: 'information',
  },
  {
    key: 'investor-relations',
    marketingUrl: '/about-lips/investor-relations',
    implementedPath: '/investor-relations',
    gatsbyLegacyUrl: '/investor-relations',
    label: 'Investor Relations',
    category: 'information',
  },
  {
    key: 'meet-our-nurses-and-care-team',
    marketingUrl: '/about-lips/meet-our-nurses-and-care-team',
    implementedPath: '/nurses',
    gatsbyLegacyUrl: '/meet-our-nurses-and-care-team',
    label: 'Nurses',
    category: 'information',
  },
  {
    key: 'health-blogs',
    marketingUrl: '/about-lips/health-blogs',
    implementedPath: '/blogs',
    gatsbyLegacyUrl: '/health/blog',
    label: 'Health & Wellness Blogs',
    category: 'information',
    matchSubpaths: true,
  },
  {
    key: 'news-and-events',
    marketingUrl: '/about-lips/news-and-events',
    implementedPath: '/news-and-events',
    gatsbyLegacyUrl: '/news-events',
    label: 'News & Events',
    category: 'information',
    matchSubpaths: true,
  },
  {
    key: 'sharkninja-event',
    marketingUrl: '/sharkninja-event',
    implementedPath: '/sharkninja',
    label: 'SharkNinja Event',
    category: 'information',
  },
  // {
  //   key: 'technogym-event',
  //   marketingUrl: '/about-lips/events/technogym-event',
  //   implementedPath: '/technogym',
  //   label: 'Technogym Event',
  //   category: 'information',
  // },
  {
    key: 'post-surgery-recovery',
    marketingUrl: '/about-lips/post-surgery-recovery',
    implementedPath: '/loveday',
    gatsbyLegacyUrl: '/loveday',
    label: 'Post-Surgery Recovery (LoveDay)',
    category: 'information',
  },

  // ── FOR PATIENTS ──────────────────────────────────────────────────────────
  {
    key: 'new-patient-information',
    marketingUrl: '/for-patients/new-patient-information',
    implementedPath: '/new-patient-information',
    gatsbyLegacyUrl: '/new-patient-information',
    label: 'New Patient Information',
    category: 'information',
  },
  {
    key: 'pricing-and-payments',
    marketingUrl: '/for-patients/pricing-and-payments',
    implementedPath: '/pricing-and-payment',
    gatsbyLegacyUrl: '/pricing-and-payment',
    label: 'Pricing and Payments',
    category: 'information',
  },
  {
    key: 'prepare-for-your-appointment',
    marketingUrl: '/for-patients/prepare-for-your-appointment',
    implementedPath: '/prepare-for-your-visit',
    gatsbyLegacyUrl: '/preparing-for-your-visit',
    label: 'Prepare for Your Appointment',
    category: 'information',
  },
  {
    key: 'patient-referrals',
    marketingUrl: '/for-patients/patient-referrals',
    implementedPath: '/for-clinicians',
    gatsbyLegacyUrl: '/for-clinicians',
    label: 'Patient Referrals',
    category: 'information',
  },
  {
    key: 'leave-feedback',
    marketingUrl: '/for-patients/leave-feedback',
    implementedPath: '/feedback',
    gatsbyLegacyUrl: '/feedback',
    label: 'Leave Feedback',
    category: 'information',
  },

  // ── FIND CARE ────────────────────────────────────────────────────────────
  {
    key: 'specialities',
    marketingUrl: '/specialities',
    implementedPath: '/specialities',
    label: 'All Specialities',
    category: 'speciality',
  },
  {
    key: 'health-packages',
    marketingUrl: '/health-packages',
    implementedPath: '/private-health-packages',
    gatsbyLegacyUrl: '/private-health-packages',
    label: 'Health Packages',
    category: 'speciality',
  },
  {
    key: 'pharmacy',
    marketingUrl: '/pharmacy',
    implementedPath: '/pharmacy',
    gatsbyLegacyUrl: '/pharmacy-battersea',
    label: 'Pharmacy',
    category: 'speciality',
  },

  // ── OUR SPECIALISTS ───────────────────────────────────────────────────────
  {
    key: 'our-specialists',
    marketingUrl: '/our-specialists',
    implementedPath: '/our-specialists',
    gatsbyLegacyUrl: '/our-specialists',
    label: 'Our Specialists',
    category: 'specialist',
    matchSubpaths: true,
  },

  // ── CONTACT & SUPPORT ────────────────────────────────────────────────────
  {
    key: 'contact-us',
    marketingUrl: '/about-lips/contact-us',
    implementedPath: '/contact-us',
    gatsbyLegacyUrl: '/contact-us',
    label: 'Contact Us',
    category: 'information',
  },
  {
    key: 'submit-complaint',
    marketingUrl: '/submit-complaint',
    implementedPath: '/submit-complaint',
    gatsbyLegacyUrl: '/submit-complaint',
    label: 'Submit a Complaint',
    category: 'information',
  },

  // ── LEGAL & POLICIES ──────────────────────────────────────────────────────
  {
    key: 'privacy-policy',
    marketingUrl: '/our-policies/privacy-policy',
    implementedPath: '/privacy-policy',
    gatsbyLegacyUrl: '/privacy-policy',
    label: 'Privacy Policy',
    category: 'legal',
  },
  {
    key: 'cookies-policy',
    marketingUrl: '/our-policies/cookies-policy',
    implementedPath: '/cookies',
    gatsbyLegacyUrl: '/cookies',
    label: 'Cookies Policy',
    category: 'legal',
  },
  {
    key: 'terms-and-conditions',
    marketingUrl: '/our-policies/terms-and-conditions',
    implementedPath: '/terms-and-conditions',
    gatsbyLegacyUrl: '/terms-and-conditions',
    label: 'Terms & Conditions',
    category: 'legal',
  },
  {
    key: 'terms-introduction-services',
    marketingUrl: '/our-policies/terms-and-conditions/introduction-services',
    implementedPath: '/terms-and-conditions/introduction-services',
    gatsbyLegacyUrl: '/terms-and-conditions/introduction-services',
    label: 'Terms - Introduction Services',
    category: 'legal',
  },
  {
    key: 'terms-medical-surgical',
    marketingUrl:
      '/our-policies/terms-and-conditions/medical-and-surgical-procedures',
    implementedPath: '/terms-and-conditions/medical-and-surgical-procedures',
    gatsbyLegacyUrl: '/terms-and-conditions/medical-and-surgical-procedures',
    label: 'Terms - Medical & Surgical Procedures',
    category: 'legal',
  },
  {
    key: 'terms-returns-refunds',
    marketingUrl:
      '/our-policies/terms-and-conditions/returns-refunds-and-cancellations',
    implementedPath: '/terms-and-conditions/returns-refunds-and-cancellations',
    label: 'Terms - Returns, Refunds & Cancellations',
    category: 'legal',
  },
  {
    key: 'complaint-policy',
    marketingUrl: '/our-policies/complaint-policy',
    implementedPath: '/complaint-policy',
    gatsbyLegacyUrl: '/complaint-policy',
    label: 'Complaint Policy',
    category: 'legal',
  },
  {
    key: 'cancellation-policy',
    marketingUrl: '/our-policies/cancellation-policy',
    implementedPath: '/cancellation-policy',
    gatsbyLegacyUrl: '/cancellation-policy',
    label: 'Cancellation Policy',
    category: 'legal',
  },

  // ── SPECIALITIES ──────────────────────────────────────────────────────────
  // Tests & Scans
  {
    key: 'speciality-blood-tests',
    marketingUrl: '/specialities/blood-tests',
    implementedPath: '/blood-test',
    gatsbyLegacyUrl: '/blood-tests',
    label: 'Blood Tests',
    category: 'speciality',
  },
  {
    key: 'speciality-bone-scan',
    marketingUrl: '/specialities/bone-scan',
    implementedPath: '/bone-scan',
    gatsbyLegacyUrl: '/bone-scan',
    label: 'Bone Scan',
    category: 'speciality',
  },
  {
    key: 'speciality-liver-health-fibroscan',
    marketingUrl: '/specialities/liver-health-fibroscan',
    implementedPath: '/liver-health-liver-scans',
    gatsbyLegacyUrl: '/liver-health-liver-scans',
    label: 'Liver Health (FibroScan)',
    category: 'speciality',
  },
  {
    key: 'speciality-radiology',
    marketingUrl: '/specialities/imaging',
    implementedPath: '/radiology',
    gatsbyLegacyUrl: '/radiology',
    label: 'Imaging',
    category: 'speciality',
  },
  {
    key: 'speciality-ultrasound',
    marketingUrl: '/specialities/ultrasound',
    implementedPath: '/ultrasound',
    gatsbyLegacyUrl: '/ultrasound',
    label: 'Ultrasound',
    category: 'speciality',
  },

  // Everyday Specialities
  {
    key: 'speciality-aesthetics-clinic',
    marketingUrl: '/specialities/aesthetics-clinic',
    implementedPath: '/aesthetics-clinic',
    gatsbyLegacyUrl: '/aesthetics-clinic',
    label: 'Aesthetics Clinic',
    category: 'speciality',
  },
  {
    key: 'speciality-audiology',
    marketingUrl: '/specialities/audiology-hearing-test-battersea-london',
    implementedPath: '/audiology-hearing-test-battersea-london',
    gatsbyLegacyUrl: '/audiology-hearing-test-battersea-london',
    label: 'Audiology (Hearing Test)',
    category: 'speciality',
  },
  {
    key: 'speciality-dental',
    marketingUrl: '/specialities/dental',
    implementedPath: '/dental-services',
    gatsbyLegacyUrl: '/dental-services',
    label: 'Dental',
    category: 'speciality',
  },
  {
    key: 'speciality-physiotherapy',
    marketingUrl: '/specialities/physiotherapy',
    implementedPath: '/physiotherapy',
    gatsbyLegacyUrl: '/physiotherapy',
    label: 'Physiotherapy',
    category: 'speciality',
  },
  {
    key: 'longevity-centre',
    marketingUrl: '/specialities/longevity-centre',
    implementedPath: '/longevity-centre',
    label: 'The Longevity Centre',
    category: 'speciality',
  },

  // Popular Specialities
  {
    key: 'speciality-breast-clinic',
    marketingUrl: '/specialities/breast-clinic',
    implementedPath: '/breast-clinic',
    gatsbyLegacyUrl: '/breast-clinic-battersea',
    label: 'Breast Clinic',
    category: 'speciality',
  },
  {
    key: 'tmj-specialist-london',
    marketingUrl: '/specialities/tmj-specialist-london',
    implementedPath: '/tmj-specialist-london',
    gatsbyLegacyUrl: '/tmj-specialist-london',
    label: 'Jaw Pain & TMJ',
    category: 'speciality',
  },
  {
    key: 'emg-nerve-conduction-test',
    marketingUrl: '/specialities/emg-nerve-conduction-test',
    implementedPath: '/emg-nerve-conduction-test',
    gatsbyLegacyUrl: '/emg-nerve-conduction-test',
    label: 'EMG & Nerve Conduction Test',
    category: 'speciality',
  },
  {
    key: 'nutritionist-london',
    marketingUrl: '/specialities/nutritionist-london',
    implementedPath: '/nutritionist-london',
    gatsbyLegacyUrl: '/nutritionist-london',
    label: 'Nutrition',
    category: 'speciality',
  },
  {
    key: 'gut-health-test',
    marketingUrl: '/specialities/gut-health-test',
    implementedPath: '/gut-health-test',
    gatsbyLegacyUrl: '/gut-health-test',
    label: 'Gut Health Test',
    category: 'speciality',
  },
  {
    key: 'baby-hip-clinic',
    marketingUrl: '/specialities/baby-hip-clinic',
    implementedPath: '/baby-hip-clinic',
    gatsbyLegacyUrl: '/baby-hip-clinic',
    label: 'Baby Hip Clinic',
    category: 'speciality',
  },
  {
    key: 'hearing-aids-london',
    marketingUrl: '/specialities/hearing-aids-london',
    implementedPath: '/hearing-aids-london',
    gatsbyLegacyUrl: '/hearing-aids-london',
    label: 'Hearing Aids',
    category: 'speciality',
  },
  {
    key: 'iron-infusion-london',
    marketingUrl: '/specialities/iron-infusion-london',
    implementedPath: '/iron-infusion-london',
    gatsbyLegacyUrl: '/iron-infusion-london',
    label: 'Iron Infusion',
    category: 'speciality',
  },
  {
    key: 'speciality-private-breast-clinic',
    marketingUrl: '/specialities/private-breast-clinic',
    implementedPath: '/breast-clinic',
    gatsbyLegacyUrl: '/private-breast-clinic',
    label: 'Breast Clinic (Private)',
    category: 'speciality',
  },
  {
    key: 'speciality-cardiology',
    marketingUrl: '/specialities/cardiology',
    implementedPath: '/specialities/cardiology',
    gatsbyLegacyUrl: '/cardiology',
    label: 'Cardiology',
    category: 'speciality',
  },
  {
    key: 'speciality-childrens-health',
    marketingUrl: '/specialities/childrens-health-paediatrics',
    implementedPath: '/private-paediatrician',
    label: "Children's Health (Paediatrics)",
    category: 'speciality',
  },
  {
    key: 'speciality-dermatology',
    marketingUrl: '/specialities/dermatology',
    implementedPath: '/dermatology',
    gatsbyLegacyUrl: '/dermatology',
    label: 'Dermatology',
    category: 'speciality',
  },
  {
    key: 'speciality-dietetics',
    marketingUrl: '/specialities/dietetics',
    implementedPath: '/dietetics',
    gatsbyLegacyUrl: '/dietetics',
    label: 'Dietetics',
    category: 'speciality',
  },
  {
    key: 'speciality-ent',
    marketingUrl: '/specialities/ent',
    implementedPath: '/ent',
    gatsbyLegacyUrl: '/ent',
    label: 'ENT (Ear, Nose and Throat)',
    category: 'speciality',
  },
  {
    key: 'speciality-exroid',
    marketingUrl: '/specialities/exroid',
    implementedPath: '/exroid',
    gatsbyLegacyUrl: '/haemorrhoids/exroid',
    label: 'eXroid (Haemorrhoid Treatment)',
    category: 'speciality',
  },
  {
    key: 'speciality-female-fertility',
    marketingUrl: '/specialities/female-fertility',
    implementedPath: '/fertility',
    gatsbyLegacyUrl: '/fertility',
    label: 'Female Fertility',
    category: 'speciality',
  },
  {
    key: 'speciality-gastric-alimetry',
    marketingUrl: '/specialities/gastric-alimetry',
    implementedPath: '/gastric-alimetry',
    gatsbyLegacyUrl: '/gastric-alimetry',
    label: 'Gastric Alimetry (Chronic Stomach Symptoms)',
    category: 'speciality',
  },
  {
    key: 'speciality-gastroenterology',
    marketingUrl: '/specialities/gastroenterology',
    implementedPath: '/gastroenterology',
    gatsbyLegacyUrl: '/gastroenterology',
    label: 'Gastroenterology',
    category: 'speciality',
  },
  {
    key: 'speciality-general-surgery',
    marketingUrl: '/specialities/general-surgery',
    implementedPath: '/general-surgery',
    gatsbyLegacyUrl: '/general-surgery',
    label: 'General Surgery',
    category: 'speciality',
  },
  {
    key: 'speciality-general-practice',
    marketingUrl: '/specialities/private-gp',
    implementedPath: '/private-gp',
    gatsbyLegacyUrl: '/private-gp',
    label: 'General Practice (GP)',
    category: 'speciality',
  },
  {
    key: 'speciality-geriatrics',
    marketingUrl: '/specialities/geriatrics',
    implementedPath: '/geriatrics',
    gatsbyLegacyUrl: '/geriatrics',
    label: 'Geriatrics',
    category: 'speciality',
  },
  {
    key: 'speciality-gynaecology-obstetrics',
    marketingUrl: '/specialities/gynaecology-and-obstetrics',
    implementedPath: '/gynaecology-and-obstetrics',
    gatsbyLegacyUrl: '/gynaecology-and-obstetrics',
    label: 'Gynaecology & Obstetrics',
    category: 'speciality',
  },
  {
    key: 'speciality-haematology',
    marketingUrl: '/specialities/haematology',
    implementedPath: '/haematology',
    gatsbyLegacyUrl: '/haematology',
    label: 'Haematology',
    category: 'speciality',
  },
  {
    key: 'speciality-hip-knee-replacement',
    marketingUrl: '/specialities/hip-and-knee-replacement',
    implementedPath: '/hip-and-knee',
    gatsbyLegacyUrl: '/hip-replacement-and-knee-replacement',
    label: 'Hip & Knee Replacement',
    category: 'speciality',
  },
  {
    key: 'speciality-hip-replacement-and-knee-replacement',
    marketingUrl: '/specialities/hip-replacement-and-knee-replacement',
    implementedPath: '/hip-and-knee',
    gatsbyLegacyUrl: '/hip-replacement-and-knee-replacement',
    label: 'Hip & Knee Replacement (Search)',
    category: 'speciality',
  },
  {
    key: 'speciality-knee-osteoarthritis',
    marketingUrl: '/specialities/knee-osteoarthritis',
    implementedPath: '/arthrosamid',
    gatsbyLegacyUrl: '/arthrosamid',
    label: 'Knee Osteoarthritis (Arthrosamid)',
    category: 'speciality',
  },
  {
    key: 'speciality-maxillofacial',
    marketingUrl: '/specialities/maxillofacial',
    implementedPath: '/maxillofacial',
    gatsbyLegacyUrl: '/maxillofacial',
    label: 'Maxillofacial',
    category: 'speciality',
  },
  {
    key: 'speciality-menopause',
    marketingUrl: '/specialities/menopause',
    implementedPath: '/menopause-clinic',
    label: 'Menopause',
    category: 'speciality',
  },
  {
    key: 'speciality-mole-mapping',
    marketingUrl: '/specialities/mole-mapping',
    implementedPath: '/mole-mapping',
    gatsbyLegacyUrl: '/mole-mapping',
    label: 'Mole Mapping',
    category: 'speciality',
  },
  {
    key: 'speciality-neurology',
    marketingUrl: '/specialities/neurology',
    implementedPath: '/neurology',
    gatsbyLegacyUrl: '/neurology',
    label: 'Neurology',
    category: 'speciality',
  },
  {
    key: 'speciality-neurosurgery',
    marketingUrl: '/specialities/spine-and-neurosurgery',
    implementedPath: '/neurosurgery',
    gatsbyLegacyUrl: '/neurosurgery',
    label: 'Spine & Neurosurgery',
    category: 'speciality',
  },
  {
    key: 'speciality-obstructive-sleep-apnoea',
    marketingUrl: '/specialities/obstructive-sleep-apnoea',
    implementedPath: '/sleep-apnoea',
    label: 'Obstructive Sleep Apnoea (Respiratory)',
    category: 'speciality',
  },
  {
    key: 'speciality-ophthalmology',
    marketingUrl: '/specialities/ophthalmology',
    implementedPath: '/ophthalmology',
    gatsbyLegacyUrl: '/ophthalmology',
    label: 'Ophthalmology',
    category: 'speciality',
  },
  {
    key: 'speciality-plastic-surgery',
    marketingUrl: '/specialities/plastic-surgery',
    implementedPath: '/plastic-surgery',
    gatsbyLegacyUrl: '/plastic-surgery',
    label: 'Plastic Surgery',
    category: 'speciality',
  },
  {
    key: 'speciality-psychiatry',
    marketingUrl: '/specialities/psychiatry',
    implementedPath: '/psychiatry',
    gatsbyLegacyUrl: '/psychiatry',
    label: 'Psychiatry',
    category: 'speciality',
  },
  {
    key: 'speciality-rheumatology',
    marketingUrl: '/specialities/rheumatology',
    implementedPath: '/rheumatology',
    gatsbyLegacyUrl: '/rheumatology',
    label: 'Rheumatology',
    category: 'speciality',
  },
  {
    key: 'speciality-sexual-health',
    marketingUrl: '/specialities/sexual-health',
    implementedPath: '/sexual-health',
    gatsbyLegacyUrl: '/sexual-health',
    label: 'Sexual Health',
    category: 'speciality',
  },
  {
    key: 'speciality-skin-lumps-bumps',
    marketingUrl: '/specialities/skin-lumps-and-bumps',
    implementedPath: '/lumps-and-bumps',
    gatsbyLegacyUrl: '/lumps-and-bumps',
    label: 'Skin Lumps & Bumps',
    category: 'speciality',
  },
  {
    key: 'speciality-trauma-orthopaedics-lower',
    marketingUrl: '/specialities/trauma-and-orthopaedics-lower-limb',
    implementedPath: '/trauma-and-orthopaedics-lower-limb',
    gatsbyLegacyUrl: '/trauma-and-orthopaedics-lower-limb',
    label: 'Trauma & Orthopaedics (Lower Limb)',
    category: 'speciality',
  },
  {
    key: 'speciality-trauma-orthopaedics-upper',
    marketingUrl: '/specialities/trauma-and-orthopaedics-upper-limb',
    implementedPath: '/trauma-and-orthopaedics-upper-limb',
    gatsbyLegacyUrl: '/trauma-and-orthopaedics-upper-limb',
    label: 'Trauma & Orthopaedics (Upper Limb)',
    category: 'speciality',
  },
  {
    key: 'speciality-trt',
    marketingUrl: '/specialities/trt',
    implementedPath: '/trt',
    gatsbyLegacyUrl: '/trt',
    label: 'TRT (Testosterone Replacement Therapy)',
    category: 'speciality',
  },
  {
    key: 'speciality-urology-andrology',
    marketingUrl: '/specialities/urology-and-andrology',
    implementedPath: '/urology-and-andrology',
    gatsbyLegacyUrl: '/urology-and-andrology',
    label: 'Urology & Andrology',
    category: 'speciality',
  },
  {
    key: 'speciality-urgent-care',
    marketingUrl: '/specialities/urgent-care-centre',
    implementedPath: '/emergency-clinic-london',
    label: 'Urgent Care Centre',
    category: 'speciality',
  },

  // ── LANDING PAGES (SEO) ──────────────────────────────────────────────────
  {
    key: 'paediatric-clinic',
    marketingUrl: '/paediatric-clinic',
    implementedPath: '/paediatric-clinic',
    label: 'Paediatric Clinic',
    category: 'speciality',
  },
  {
    key: 'private-ultrasound-scans',
    marketingUrl: '/private-ultrasound-scans',
    implementedPath: '/private-ultrasound-scans',
    label: 'Private Ultrasound Scans',
    category: 'speciality',
  },
  {
    key: 'private-ultrasound-scans-battersea',
    marketingUrl: '/private-ultrasound-scans-Battersea',
    implementedPath: '/private-ultrasound-scans-battersea',
    label: 'Private Ultrasound Scans - Battersea',
    category: 'speciality',
  },
  {
    key: 'female-ultrasound',
    marketingUrl: '/female-ultrasound',
    implementedPath: '/female-ultrasound',
    label: 'Female Ultrasound Scans',
    category: 'speciality',
  },
  {
    key: 'gp-access',
    marketingUrl: '/gp-access',
    implementedPath: '/gp-access',
    label: 'GP Access',
    category: 'speciality',
  },
  {
    key: 'urgent-care',
    marketingUrl: '/urgent-care',
    implementedPath: '/urgent-care',
    label: 'Urgent Care',
    category: 'speciality',
  },
  {
    key: 'blood-tests-landing',
    marketingUrl: '/blood-tests',
    implementedPath: '/blood-tests',
    label: 'Blood Tests Landing Page',
    category: 'speciality',
  },
  {
    key: 'bone-scan-clinic',
    marketingUrl: '/bone-scan-clinic',
    implementedPath: '/bone-scan-clinic',
    label: 'Bone Scan Clinic',
    category: 'speciality',
  },
  {
    key: 'sleep-apnoea-clinic',
    marketingUrl: '/sleep-apnoea-clinic',
    implementedPath: '/sleep-apnoea-clinic',
    label: 'Sleep Apnoea Clinic',
    category: 'speciality',
  },

  // ── CONDITIONS (SEO Landing Pages) ────────────────────────────────────────
  // Note: Conditions pages are auto-generated. Only include key popular ones here
  {
    key: 'condition-adhd',
    marketingUrl: '/conditions/adhd',
    implementedPath: '/conditions/adhd',
    label: 'ADHD',
    category: 'condition',
  },
  {
    key: 'condition-back-pain',
    marketingUrl: '/conditions/back-pain-management',
    implementedPath: '/conditions/back-pain-management',
    label: 'Back Pain Management',
    category: 'condition',
  },

  // ── TESTING/UTILITIES ─────────────────────────────────────────────────────
  {
    key: 'testing',
    marketingUrl: '/testing',
    implementedPath: '/testing',
    label: 'Testing',
    category: 'other',
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// LOOKUP FUNCTIONS - Efficient access by key, URL, or path
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get route entry by unique key
 */
export function getRouteByKey(key: string): RouteMappingEntry | undefined {
  return ROUTES_MAPPING.find((route) => route.key === key);
}

/**
 * Get route entry by marketing URL
 */
export function getRouteByMarketingUrl(
  url: string,
): RouteMappingEntry | undefined {
  return ROUTES_MAPPING.find((route) => route.marketingUrl === url);
}

/**
 * Get route entry by implemented path
 */
export function getRouteByImplementedPath(
  path: string,
): RouteMappingEntry | undefined {
  return ROUTES_MAPPING.find((route) => route.implementedPath === path);
}

/**
 * Get all routes in a specific category
 */
export function getRoutesByCategory(
  category: RouteMappingEntry['category'],
): RouteMappingEntry[] {
  return ROUTES_MAPPING.filter((route) => route.category === category);
}

/**
 * Get all speciality routes
 */
export function getAllSpecialities(): RouteMappingEntry[] {
  return getRoutesByCategory('speciality').sort((a, b) =>
    a.label.localeCompare(b.label),
  );
}

/**
 * Resolve a given URL to its clean marketing/sitemap canonical URL from ROUTES_MAPPING.
 * If no mapping exists, returns the URL unchanged.
 */
export function resolveMarketingUrl(
  url: string | undefined,
): string | undefined {
  if (!url) return url;

  // Preserve external, fragment, and mailto/tel links
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('#') ||
    url.startsWith('mailto:') ||
    url.startsWith('tel:')
  ) {
    return url;
  }

  // Split query parameters if any
  const [pathWithSlash, query] = url.split('?');

  // Normalize path: strip trailing slash except for root '/'
  const normalizedPath =
    pathWithSlash.endsWith('/') && pathWithSlash.length > 1
      ? pathWithSlash.slice(0, -1)
      : pathWithSlash;

  // 1. Exact match in ROUTES_MAPPING for either implementedPath or marketingUrl
  const exactMatch = ROUTES_MAPPING.find(
    (entry) =>
      entry.implementedPath === normalizedPath ||
      entry.marketingUrl === normalizedPath,
  );
  if (exactMatch) {
    const matchedUrl = exactMatch.marketingUrl;
    return query ? `${matchedUrl}?${query}` : matchedUrl;
  }

  // 2. Subpath matches (matchSubpaths: true)
  const subpathMatch = ROUTES_MAPPING.filter((entry) => entry.matchSubpaths)
    .sort((a, b) => b.implementedPath.length - a.implementedPath.length)
    .find((entry) => normalizedPath.startsWith(entry.implementedPath + '/'));
  if (subpathMatch) {
    const subpath = normalizedPath.slice(subpathMatch.implementedPath.length);
    const matchedUrl = `${subpathMatch.marketingUrl}${subpath}`;
    return query ? `${matchedUrl}?${query}` : matchedUrl;
  }

  // 3. Fallback: If no mapping found, return original URL
  return url;
}
